// src/pages/finance-portal/vendors/VendorForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert,
    CircularProgress, Divider, Autocomplete,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, TablePagination, Skeleton, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';
import { useFinanceAccount } from '../../../contexts/FinanceAccountContext';
import { useChatbots } from '../../../contexts/ChatbotContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_TERMS_OPTIONS = [
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_60', label: 'Net 60' },
    { value: 'NET_90', label: 'Net 90' },
];

const PAYMENT_METHOD_OPTIONS = [
    { value: 'ACH', label: 'ACH' },
    { value: 'CHECK', label: 'Check' },
    { value: 'WIRE', label: 'Wire Transfer' },
    { value: 'VIRTUAL_CARD', label: 'Virtual Card' },
];

const W9_STATUS_OPTIONS = [
    { value: 'NOT_RECEIVED', label: 'Not Received' },
    { value: 'REQUESTED', label: 'Requested' },
    { value: 'RECEIVED', label: 'Received' },
];

// Approved Vendor List
const QUALIFICATION_STATUS_OPTIONS = [
    { value: 'NOT_QUALIFIED', label: 'Not Qualified' },
    { value: 'PENDING',       label: 'Pending Review' },
    { value: 'QUALIFIED',     label: 'Qualified' },
    { value: 'EXPIRED',       label: 'Expired' },
];
const INSURANCE_STATUS_OPTIONS = [
    { value: 'NOT_VERIFIED', label: 'Not Verified' },
    { value: 'VERIFIED',     label: 'Verified' },
    { value: 'EXPIRED',      label: 'Expired' },
];

const emptyAddress = { street: '', city: '', state: '', zip: '', country: '' };

const initialFormData = {
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    website: '',
    payment_terms: 'NET_30',
    preferred_payment_method: 'ACH',
    currency: 'USD',
    default_gl_account_id: null,
    tax_id: '',
    w9_status: 'NOT_RECEIVED',
    billing_address: { ...emptyAddress },
    notes: '',
    tags: '',
    crm_contact_id: null,
    // AVL (only sent on edit via separate /qualification endpoint)
    qualification_status: 'NOT_QUALIFIED',
    qualified_at: '',
    qualification_expires_at: '',
    approved_categories: '',
    quality_rating: '',
    certifications: '',
    insurance_status: 'NOT_VERIFIED',
    insurance_expires_at: '',
    diversity_classification: '',
};

export default function VendorForm({ open, onClose, onSaved, vendor }) {
    const { enqueueSnackbar } = useSnackbar();
    const { selectedAccount } = useFinanceAccount();
    const { activeChatbot } = useChatbots();
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({ ...initialFormData });
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // GL Account autocomplete
    const [glAccounts, setGlAccounts] = useState([]);
    const [glLoading, setGlLoading] = useState(false);
    const [selectedGlAccount, setSelectedGlAccount] = useState(null);

    // CRM contact selection
    const [step, setStep] = useState('select'); // 'select' | 'form'
    const [crmContacts, setCrmContacts] = useState([]);
    const [crmLoading, setCrmLoading] = useState(false);
    const [crmSearch, setCrmSearch] = useState('');
    const [crmPage, setCrmPage] = useState(0);
    const [crmTotal, setCrmTotal] = useState(0);
    const [selectedContact, setSelectedContact] = useState(null);

    const isEdit = !!vendor;

    const fetchGlAccounts = useCallback(async () => {
        setGlLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) setGlAccounts(res.data.data || []);
        } catch (err) { /* silent */ }
        finally { setGlLoading(false); }
    }, []);

    const fetchCrmContacts = useCallback(async () => {
        setCrmLoading(true);
        try {
            const params = { page: crmPage + 1, per_page: 10 };
            if (crmSearch) params.search = crmSearch;
            const res = await axiosInstance.get('/api/finance/ap-crm-contacts', { params });
            if (res.data.success) {
                setCrmContacts(res.data.data || []);
                setCrmTotal(res.data.total || 0);
            }
        } catch (err) { /* silent */ }
        finally { setCrmLoading(false); }
    }, [crmPage, crmSearch]);

    useEffect(() => {
        if (open) fetchGlAccounts();
    }, [open, fetchGlAccounts]);

    useEffect(() => {
        if (open && !isEdit && step === 'select') fetchCrmContacts();
    }, [open, isEdit, step, fetchCrmContacts]);

    useEffect(() => {
        if (open) {
            setError(null);
            if (vendor) {
                setStep('form');
                setSelectedContact(vendor.crm_contact_id ? {
                    id: vendor.crm_contact_id,
                    full_name: vendor.crm_contact_name,
                    email: vendor.crm_contact_email,
                } : null);
                setFormData({
                    company_name: vendor.company_name || '',
                    first_name: vendor.first_name || '',
                    last_name: vendor.last_name || '',
                    email: vendor.email || '',
                    phone: vendor.phone || '',
                    website: vendor.website || '',
                    payment_terms: vendor.payment_terms || 'NET_30',
                    preferred_payment_method: vendor.preferred_payment_method || 'ACH',
                    currency: vendor.currency || 'USD',
                    default_gl_account_id: vendor.default_gl_account_id || null,
                    tax_id: vendor.tax_id || '',
                    w9_status: vendor.w9_status || 'NOT_RECEIVED',
                    billing_address: vendor.billing_address
                        ? (typeof vendor.billing_address === 'string' ? JSON.parse(vendor.billing_address) : { ...emptyAddress, ...vendor.billing_address })
                        : { ...emptyAddress },
                    notes: vendor.notes || '',
                    tags: Array.isArray(vendor.tags) ? vendor.tags.join(', ') : (vendor.tags || ''),
                    crm_contact_id: vendor.crm_contact_id || null,
                    qualification_status: vendor.qualification_status || 'NOT_QUALIFIED',
                    qualified_at: vendor.qualified_at || '',
                    qualification_expires_at: vendor.qualification_expires_at || '',
                    approved_categories: Array.isArray(vendor.approved_categories)
                        ? vendor.approved_categories.join(', ')
                        : (vendor.approved_categories || ''),
                    quality_rating: vendor.quality_rating != null ? String(vendor.quality_rating) : '',
                    certifications: Array.isArray(vendor.certifications)
                        ? vendor.certifications.join(', ')
                        : (vendor.certifications || ''),
                    insurance_status: vendor.insurance_status || 'NOT_VERIFIED',
                    insurance_expires_at: vendor.insurance_expires_at || '',
                    diversity_classification: vendor.diversity_classification || '',
                });
                if (vendor.default_gl_account_id && vendor.default_gl_account) {
                    setSelectedGlAccount(vendor.default_gl_account);
                } else {
                    setSelectedGlAccount(null);
                }
            } else {
                setStep('select');
                setSelectedContact(null);
                setCrmSearch('');
                setCrmPage(0);
                setFormData({ ...initialFormData, billing_address: { ...emptyAddress } });
                setSelectedGlAccount(null);
            }
        }
    }, [open, vendor]);

    useEffect(() => {
        if (isEdit && vendor?.default_gl_account_id && glAccounts.length > 0 && !selectedGlAccount) {
            const found = glAccounts.find(a => a.id === vendor.default_gl_account_id);
            if (found) setSelectedGlAccount(found);
        }
    }, [isEdit, vendor, glAccounts, selectedGlAccount]);

    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setFormData(prev => ({
            ...prev,
            crm_contact_id: contact.id,
            first_name: contact.first_name || '',
            last_name: contact.last_name || '',
            email: contact.email || '',
            phone: contact.phone || '',
            company_name: contact.company_name || '',
        }));
        setStep('form');
    };

    const handleSkipCrm = () => {
        setSelectedContact(null);
        setFormData({ ...initialFormData, billing_address: { ...emptyAddress }, crm_contact_id: null });
        setStep('form');
    };

    const handleAddCrmContact = () => {
        const slug = activeChatbot?.chatbot_identity || activeChatbot?.id;
        if (!slug) {
            enqueueSnackbar('Please select a chatbot first to add a CRM contact', { variant: 'warning' });
            return;
        }
        onClose();
        navigate(`/chatbot/${slug}/crm/contacts`, {
            state: {
                fromFinancePortal: true,
                preselectContactType: 'VENDOR',
                returnPath: location.pathname + location.search,
                returnAction: 'addVendor',
                reason: 'Add a CRM contact to link as an AP vendor.',
            },
        });
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };
    const updateAddress = (field, value) => setFormData(prev => ({
        ...prev,
        billing_address: { ...prev.billing_address, [field]: value },
    }));

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const tagsArray = formData.tags
                ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
                : [];

            const payload = {
                company_name: formData.company_name || null,
                first_name: formData.first_name || null,
                last_name: formData.last_name || null,
                email: formData.email || null,
                phone: formData.phone || null,
                website: formData.website || null,
                payment_terms: formData.payment_terms,
                preferred_payment_method: formData.preferred_payment_method,
                currency: formData.currency,
                default_gl_account_id: selectedGlAccount?.id || null,
                tax_id: formData.tax_id || null,
                w9_status: formData.w9_status,
                billing_address: formData.billing_address,
                notes: formData.notes || null,
                tags: tagsArray,
            };

            if (!isEdit && formData.crm_contact_id) {
                payload.crm_contact_id = formData.crm_contact_id;
            }
            // Tag to globally selected CRM account on create
            if (!isEdit && selectedAccount?.id) {
                payload.crm_account_id = selectedAccount.id;
            }

            if (isEdit) {
                await axiosInstance.put(`/api/finance/vendors/${vendor.id}`, payload);
                // push qualification metadata via dedicated endpoint
                const splitList = (s) =>
                    s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
                const qualPayload = {
                    qualification_status: formData.qualification_status,
                    qualified_at: formData.qualified_at || null,
                    qualification_expires_at: formData.qualification_expires_at || null,
                    approved_categories: splitList(formData.approved_categories),
                    quality_rating: formData.quality_rating === ''
                        ? null
                        : Math.max(1, Math.min(5, parseInt(formData.quality_rating, 10))),
                    certifications: splitList(formData.certifications),
                    insurance_status: formData.insurance_status,
                    insurance_expires_at: formData.insurance_expires_at || null,
                    diversity_classification: formData.diversity_classification || null,
                };
                try {
                    await axiosInstance.put(
                        `/api/finance/vendors/${vendor.id}/qualification`,
                        qualPayload,
                    );
                } catch (e) {
                    enqueueSnackbar(
                        e.response?.data?.message ||
                            'Vendor saved, but qualification update failed',
                        { variant: 'warning' },
                    );
                }
                enqueueSnackbar('Vendor updated successfully', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/vendors', payload);
                enqueueSnackbar('Vendor created successfully', { variant: 'success' });
            }

            onSaved?.();
            onClose();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save vendor';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar(msg, { variant: 'warning' });
            } else {
                setError(msg);
                enqueueSnackbar(msg, { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    // ---- CRM Contact Selection Step ----
    const renderCrmSelection = () => (
        <>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonSearchIcon sx={{ color: ACCENT }} />
                    Select CRM Contact (Vendor)
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', fontFamily, fontSize: '0.8rem' }}>
                    Select a CRM contact to link as an AP vendor. Their name, email, and phone will be auto-populated. Don't see your contact? Click <strong>Add to CRM</strong> below to create one in the CRM, then come back here to link it.
                </Alert>

                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search contacts by name, email, or phone..."
                    value={crmSearch}
                    onChange={e => { setCrmSearch(e.target.value); setCrmPage(0); }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                        sx: { fontFamily, borderRadius: '10px' },
                    }}
                    sx={{ mb: 2 }}
                />

                <TableContainer sx={{ maxHeight: 380 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {['Name', 'Email', 'Phone', 'Company', ''].map(h => (
                                    <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', bgcolor: '#f8fafc' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {crmLoading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}><TableCell colSpan={5}><Skeleton height={32} /></TableCell></TableRow>
                                ))
                            ) : crmContacts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                            {crmSearch ? 'No contacts found matching your search' : 'No CRM contacts available'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : crmContacts.map(contact => (
                                <TableRow
                                    key={contact.id}
                                    hover
                                    onClick={() => !contact.already_linked && handleSelectContact(contact)}
                                    sx={{
                                        cursor: contact.already_linked ? 'default' : 'pointer',
                                        opacity: contact.already_linked ? 0.5 : 1,
                                        '&:hover': { bgcolor: contact.already_linked ? 'inherit' : '#f0fdf4' },
                                    }}
                                >
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
                                        {contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{contact.email || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{contact.phone || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{contact.company_name || '—'}</TableCell>
                                    <TableCell>
                                        {contact.already_linked ? (
                                            <Chip label="Already Linked" size="small" sx={{ fontFamily, fontSize: '0.65rem', height: 20, bgcolor: '#f1f5f9', color: '#94a3b8' }} />
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => { e.stopPropagation(); handleSelectContact(contact); }}
                                                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: ACCENT, color: ACCENT, minWidth: 'auto', px: 1.5, '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' } }}
                                            >
                                                Select
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {crmTotal > 10 && (
                    <TablePagination
                        component="div"
                        count={crmTotal}
                        page={crmPage}
                        onPageChange={(_, p) => setCrmPage(p)}
                        rowsPerPage={10}
                        rowsPerPageOptions={[10]}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Button
                    onClick={handleAddCrmContact}
                    startIcon={<PersonAddIcon />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    disabled={!activeChatbot}
                    sx={{
                        fontFamily, textTransform: 'none', fontWeight: 600,
                        borderRadius: '10px', color: '#1d4ed8', bgcolor: '#dbeafe',
                        px: 1.75, py: 0.75,
                        '&:hover': { bgcolor: '#bfdbfe' },
                    }}
                >
                    Add to CRM
                </Button>
                <Button onClick={handleSkipCrm} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Create Without CRM Link
                </Button>
            </DialogActions>
        </>
    );

    // ---- Vendor Form Step ----
    const renderForm = () => (
        <>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!isEdit && (
                        <IconButton size="small" onClick={() => { setStep('select'); setSelectedContact(null); }} sx={{ color: '#94a3b8', mr: 0.5 }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    {isEdit ? 'Edit Vendor' : 'New Vendor'}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '10px', fontFamily }}>{error}</Alert>}

                {selectedContact && (
                    <Alert
                        severity="success"
                        icon={<LinkIcon sx={{ color: ACCENT }} />}
                        sx={{ mb: 2, borderRadius: '10px', fontFamily, fontSize: '0.8rem', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}
                    >
                        Linked to CRM Contact: <strong>{selectedContact.full_name || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}</strong>
                        {selectedContact.email && ` (${selectedContact.email})`}
                    </Alert>
                )}

                {/* Basic Info */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Basic Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <TextField label="Company Name" size="small" fullWidth
                        value={formData.company_name}
                        onChange={e => updateField('company_name', e.target.value)}
                        error={Boolean(errors.company_name)} helperText={errors.company_name || ' '}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField label="First Name" size="small" fullWidth
                            value={formData.first_name}
                            onChange={e => updateField('first_name', e.target.value)}
                            error={Boolean(errors.first_name)} helperText={errors.first_name || ' '}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Last Name" size="small" fullWidth
                            value={formData.last_name}
                            onChange={e => updateField('last_name', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField label="Email" size="small" fullWidth type="email"
                            value={formData.email}
                            onChange={e => updateField('email', e.target.value)}
                            error={Boolean(errors.email)} helperText={errors.email || ' '}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Phone" size="small" fullWidth
                            value={formData.phone}
                            onChange={e => updateField('phone', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <TextField label="Website" size="small" fullWidth
                        value={formData.website}
                        onChange={e => updateField('website', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Payment Settings */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Payment Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={labelSx}>Payment Terms</InputLabel>
                            <Select value={formData.payment_terms}
                                onChange={e => updateField('payment_terms', e.target.value)}
                                label="Payment Terms"
                                sx={{ fontFamily, borderRadius: '10px' }}>
                                {PAYMENT_TERMS_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={labelSx}>Preferred Payment Method</InputLabel>
                            <Select value={formData.preferred_payment_method}
                                onChange={e => updateField('preferred_payment_method', e.target.value)}
                                label="Preferred Payment Method"
                                sx={{ fontFamily, borderRadius: '10px' }}>
                                {PAYMENT_METHOD_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField label="Currency" size="small" sx={{ minWidth: 100 }}
                            value={formData.currency}
                            onChange={e => updateField('currency', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <Autocomplete size="small" fullWidth
                            options={glAccounts}
                            getOptionLabel={opt => opt ? `${opt.account_number || ''} - ${opt.name || ''}`.trim() : ''}
                            value={selectedGlAccount}
                            onChange={(_, val) => setSelectedGlAccount(val)}
                            loading={glLoading}
                            renderInput={(params) => (
                                <TextField {...params} label="Default GL Account"
                                    InputLabelProps={{ ...params.InputLabelProps, sx: labelSx }}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px', fontSize: '0.85rem' },
                                        endAdornment: (
                                            <>
                                                {glLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }} />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="No accounts found" />
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Tax Info */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Tax Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                    <TextField label="Tax ID" size="small" fullWidth
                        value={formData.tax_id}
                        onChange={e => updateField('tax_id', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={labelSx}>W-9 Status</InputLabel>
                        <Select value={formData.w9_status}
                            onChange={e => updateField('w9_status', e.target.value)}
                            label="W-9 Status"
                            sx={{ fontFamily, borderRadius: '10px' }}>
                            {W9_STATUS_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Billing Address */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Billing Address
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <TextField label="Street" size="small" fullWidth
                        value={formData.billing_address.street}
                        onChange={e => updateAddress('street', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField label="City" size="small" fullWidth
                            value={formData.billing_address.city}
                            onChange={e => updateAddress('city', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <TextField label="State" size="small" sx={{ minWidth: 120 }}
                            value={formData.billing_address.state}
                            onChange={e => updateAddress('state', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField label="ZIP Code" size="small" sx={{ minWidth: 120 }}
                            value={formData.billing_address.zip}
                            onChange={e => updateAddress('zip', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Country" size="small" fullWidth
                            value={formData.billing_address.country}
                            onChange={e => updateAddress('country', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                </Box>

                {isEdit && (
                    <>
                        <Divider sx={{ mb: 3 }} />
                        {/* Approved Vendor List qualification */}
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem',
                                          color: '#1e293b', mb: 0.5 }}>
                            Approved Vendor List (Procurement)
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mb: 1.5 }}>
                            Qualification status & metadata used by the Procurement module.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel sx={labelSx}>Qualification Status</InputLabel>
                                    <Select value={formData.qualification_status}
                                            onChange={e => updateField('qualification_status', e.target.value)}
                                            label="Qualification Status"
                                            sx={{ fontFamily, borderRadius: '10px' }}>
                                        {QUALIFICATION_STATUS_OPTIONS.map(o =>
                                            <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <InputLabel sx={labelSx}>Insurance Status</InputLabel>
                                    <Select value={formData.insurance_status}
                                            onChange={e => updateField('insurance_status', e.target.value)}
                                            label="Insurance Status"
                                            sx={{ fontFamily, borderRadius: '10px' }}>
                                        {INSURANCE_STATUS_OPTIONS.map(o =>
                                            <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField label="Qualified On" size="small" fullWidth type="date"
                                           value={formData.qualified_at}
                                           onChange={e => updateField('qualified_at', e.target.value)}
                                           InputLabelProps={{ ...labelSx, shrink: true, sx: labelSx }}
                                           InputProps={{ sx: fieldSx }} />
                                <TextField label="Qualification Expires" size="small" fullWidth type="date"
                                           value={formData.qualification_expires_at}
                                           onChange={e => updateField('qualification_expires_at', e.target.value)}
                                           InputLabelProps={{ ...labelSx, shrink: true, sx: labelSx }}
                                           InputProps={{ sx: fieldSx }} />
                                <TextField label="Insurance Expires" size="small" fullWidth type="date"
                                           value={formData.insurance_expires_at}
                                           onChange={e => updateField('insurance_expires_at', e.target.value)}
                                           InputLabelProps={{ ...labelSx, shrink: true, sx: labelSx }}
                                           InputProps={{ sx: fieldSx }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField label="Quality Rating (1–5)" size="small" sx={{ width: 180 }} type="number"
                                           value={formData.quality_rating}
                                           onChange={e => updateField('quality_rating', e.target.value)}
                                           inputProps={{ min: 1, max: 5 }}
                                           InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                                <TextField label="Diversity Classification" size="small" fullWidth
                                           value={formData.diversity_classification}
                                           onChange={e => updateField('diversity_classification', e.target.value)}
                                           placeholder="e.g. WBE, MBE, SBE"
                                           InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                            </Box>
                            <TextField label="Approved Categories" size="small" fullWidth
                                       value={formData.approved_categories}
                                       onChange={e => updateField('approved_categories', e.target.value)}
                                       helperText="Comma-separated (e.g. Office Supplies, IT Services)"
                                       FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }}
                                       InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                            <TextField label="Certifications" size="small" fullWidth
                                       value={formData.certifications}
                                       onChange={e => updateField('certifications', e.target.value)}
                                       helperText="Comma-separated (e.g. ISO 9001, SOC 2)"
                                       FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }}
                                       InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        </Box>
                    </>
                )}

                <Divider sx={{ mb: 3 }} />

                {/* Notes & Tags */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Additional
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField label="Notes" size="small" fullWidth multiline rows={3}
                        value={formData.notes}
                        onChange={e => updateField('notes', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    <TextField label="Tags" size="small" fullWidth
                        value={formData.tags}
                        onChange={e => updateField('tags', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        helperText="Comma-separated tags (e.g. Preferred, International, Critical)"
                        FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || (!formData.company_name && !formData.first_name && !formData.last_name)}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                >
                    {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Vendor'}
                </Button>
            </DialogActions>
        </>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            {!isEdit && step === 'select' ? renderCrmSelection() : renderForm()}
        </Dialog>
    );
}
